/**
 * Modal "Acerca de" · muestra versión, changelog y roadmap.
 */
import { useState, useEffect } from "react";
import { X, CheckCircle2, Clock, Sparkles, GitCommit, Rocket } from "lucide-react";
import api from "../lib/api";
import { APP_VERSION, APP_STAGE, BUILD_DATE, CHANGELOG, ROADMAP } from "../lib/version";

export default function AboutModal({ open, onClose }) {
  const [backendVer, setBackendVer] = useState(null);

  useEffect(() => {
    if (!open) return;
    api.get("/v1/system/version").then((r) => setBackendVer(r.data)).catch(() => setBackendVer({ error: true }));
  }, [open]);

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="about-modal"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="bg-white rounded-xl w-full max-w-3xl max-h-[92vh] overflow-hidden shadow-2xl flex flex-col"
      >
        {/* Header */}
        <div className="px-6 py-4 bg-[#0A3747] text-white flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-md bg-[#E0E621] flex items-center justify-center">
              <GitCommit size={20} className="text-[#0A3747]" strokeWidth={2.5} />
            </div>
            <div>
              <div className="text-[10px] font-mono uppercase tracking-widest text-[#E0E621]">Acerca del sistema</div>
              <div className="text-lg font-bold" style={{ fontFamily: "Chivo" }}>Alfa-Field Connect</div>
            </div>
          </div>
          <button onClick={onClose} data-testid="about-close" className="p-1.5 rounded hover:bg-white/10">
            <X size={20} />
          </button>
        </div>

        {/* Info bloque */}
        <div className="px-6 py-4 bg-gradient-to-b from-[#F8FAFC] to-white border-b border-gray-100">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <VerBox label="Versión frontend" value={APP_VERSION} accent />
            <VerBox label="Hito" value={APP_STAGE} />
            <VerBox label="Fecha de build" value={BUILD_DATE} />
            <VerBox
              label="Versión backend"
              value={backendVer?.error ? "no disponible" : (backendVer?.version || "cargando...")}
              subvalue={backendVer?.stage}
            />
          </div>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-6 space-y-6">
          {/* Changelog */}
          <div>
            <h3 className="flex items-center gap-2 font-semibold text-gray-900 mb-3 uppercase tracking-wider text-xs" style={{ fontFamily: "Chivo" }}>
              <Sparkles size={14} className="text-[#0A3747]" />
              Historial de versiones
            </h3>
            <div className="space-y-4">
              {CHANGELOG.map((entry, idx) => (
                <div
                  key={entry.version}
                  className={`relative pl-8 pb-3 border-l-2 ${idx === 0 ? "border-[#E0E621]" : "border-gray-200"}`}
                  data-testid={`changelog-${entry.version}`}
                >
                  <div
                    className={`absolute -left-2 top-1 w-4 h-4 rounded-full ${idx === 0 ? "bg-[#E0E621] ring-4 ring-[#E0E621]/30" : "bg-white border-2 border-gray-300"}`}
                  />
                  <div className="flex items-baseline gap-3 flex-wrap">
                    <span className={`font-mono font-bold ${idx === 0 ? "text-[#0A3747]" : "text-gray-700"}`}>
                      v{entry.version}
                    </span>
                    {idx === 0 && (
                      <span className="text-[9px] font-mono uppercase tracking-wider px-1.5 py-0.5 rounded bg-[#0A3747] text-[#E0E621]">
                        Actual
                      </span>
                    )}
                    <span className="text-xs text-gray-500 font-mono">{entry.date}</span>
                    <span className="text-xs text-gray-600 italic">· {entry.stage}</span>
                  </div>
                  <ul className="mt-2 space-y-1">
                    {entry.highlights.map((h, i) => (
                      <li key={i} className="flex items-start gap-2 text-sm text-gray-700">
                        <CheckCircle2 size={13} className="text-emerald-600 flex-shrink-0 mt-1" />
                        <span>{h}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </div>

          {/* Roadmap */}
          <div>
            <h3 className="flex items-center gap-2 font-semibold text-gray-900 mb-3 uppercase tracking-wider text-xs" style={{ fontFamily: "Chivo" }}>
              <Rocket size={14} className="text-[#0A3747]" />
              Próximos entregables
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              {ROADMAP.map((r, i) => (
                <div key={i} className="p-3 rounded-md border border-gray-200 bg-white" data-testid={`roadmap-${r.code}`}>
                  <div className="flex items-center gap-1 mb-1">
                    <Clock size={12} className="text-amber-600" />
                    <span className="text-[10px] font-mono uppercase tracking-widest text-amber-700">{r.when}</span>
                  </div>
                  <div className="font-semibold text-sm text-gray-900 mb-2">{r.code}</div>
                  <ul className="space-y-1">
                    {r.items.map((it, j) => (
                      <li key={j} className="text-xs text-gray-700 leading-snug">• {it}</li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="px-6 py-3 border-t border-gray-100 bg-gray-50 flex items-center justify-between text-[10px] font-mono text-gray-500">
          <span>TELEALFACOM S.A. · Confidencial</span>
          <span>alfafield · analytica-ec.com</span>
        </div>
      </div>
    </div>
  );
}

function VerBox({ label, value, subvalue, accent }) {
  return (
    <div>
      <div className="text-[10px] uppercase font-mono tracking-widest text-gray-500">{label}</div>
      <div className={`font-bold mt-0.5 ${accent ? "text-2xl text-[#0A3747]" : "text-base text-gray-800"}`} style={{ fontFamily: "Chivo" }}>
        {value}
      </div>
      {subvalue && <div className="text-[10px] text-gray-500 font-mono">{subvalue}</div>}
    </div>
  );
}
